import {
  maskedModulesForEdition,
  normalizeHospitolEdition,
  type HospitolEditionId,
  type HospitolUiModuleId,
} from "@itaas/hospitol-editions";
import { routes } from "@/router/all_routes";
import {
  getStorageItem,
  setStorageItem,
  removeStorageItem,
} from "@/core/tenant/hospitol-storage";

const EDITION_KEY = "hospitol.tenantEdition";
const EDITION_EVENT = "hospitol-tenant-edition";

/** Paths masqués par module logique (WP-H3). */
const MODULE_PATHS: Record<HospitolUiModuleId, string[]> = {
  dashboard: [routes.dashboardFinance, routes.dashboardOperations],
  admissions: [routes.admissions],
  emergency: [routes.emergency, routes.ambulanceTransports],
  appointments: [routes.appointments],
  patients: [routes.patients, routes.patientRecords],
  consultations: [routes.consultations],
  prescriptions: [routes.prescriptions],
  surgery: [routes.surgery],
  nursing: [routes.nursingCare],
  hospitalization: [routes.hospitalization, routes.patientConsumables],
  pharmacy: [
    routes.pharmacyOrders,
    routes.pharmacyInvoice,
    routes.pharmacyInvoiceHistory,
    routes.pharmacyStockArticles,
    routes.pharmacyStockScan,
  ],
  laboratory: [routes.laboratory, routes.labResults],
  imaging: [routes.imaging, routes.imagingReports],
  rh: [routes.rhEmployees, routes.rhDepartments, routes.rhSchedules, routes.rhAdminConsole],
  supply: [
    routes.supplyMedicinesArticles,
    routes.supplyMedicinesLevels,
    routes.supplyMedicinesReceipts,
    routes.supplyMedicinesReceiptsHistory,
    routes.supplyConsumablesArticles,
    routes.supplyConsumablesLevels,
    routes.supplyConsumablesReceipts,
    routes.supplyConsumablesReceiptsHistory,
    routes.supplyAdminConsole,
  ],
  billing: [
    routes.billingAccounting,
    routes.billingCashier,
    routes.billingInvoices,
    routes.billingAdminConsole,
  ],
  insurance: [
    routes.insuranceHome,
    routes.insuranceInsurers,
    routes.insuranceCases,
    routes.insurancePartnerships,
  ],
  direction: [routes.hospitalServicesHub, routes.directionHub],
  admin: [
    routes.adminUsers,
    routes.adminSettings,
    routes.adminIntegrations,
    routes.hospitolSetup,
  ],
};

export function getTenantEdition(): HospitolEditionId {
  return normalizeHospitolEdition(getStorageItem(EDITION_KEY) ?? "clinic");
}

export function setTenantEdition(edition: string): void {
  const id = normalizeHospitolEdition(edition);
  setStorageItem(EDITION_KEY, id);
  window.dispatchEvent(new CustomEvent(EDITION_EVENT));
}

export function clearTenantEdition(): void {
  removeStorageItem(EDITION_KEY);
  window.dispatchEvent(new CustomEvent(EDITION_EVENT));
}

export function subscribeTenantEdition(onChange: () => void): () => void {
  const handler = () => onChange();
  window.addEventListener(EDITION_EVENT, handler);
  return () => window.removeEventListener(EDITION_EVENT, handler);
}

export function getTenantEditionSnapshot(): string {
  return getStorageItem(EDITION_KEY) ?? "clinic";
}

/** Chemins masqués par l'édition / profil (en plus du filtre démo LS). */
export function getEditionHiddenMenuPaths(edition?: HospitolEditionId): Set<string> {
  const id = edition ?? getTenantEdition();
  const masked = maskedModulesForEdition(id);
  const paths = new Set<string>();
  for (const mod of masked) {
    for (const p of MODULE_PATHS[mod] ?? []) {
      paths.add(p);
    }
  }
  // Pharmacy: garder le tableau de bord tenant, masquer le hub services hospitaliers déjà dans direction
  return paths;
}
